/**
 * Tassonomia ufficiale degli endpoint di Flowenti Robots API.
 * I path, gli esempi JSON e i metodi sono identici in tutte le lingue —
 * solo le descrizioni passano per il dict i18n (`descKey`).
 *
 * `anchorId` è l'id HTML usato dalla sidebar per lo scrollIntoView
 * e dagli spider Google per generare frammenti Sitelinks.
 *
 * `accessLevel` distingue i due stati operativi di un endpoint rispetto al
 * robot runtime (vedi badge in RobotsHelp.tsx):
 *   - "whitelist"              → invocabile oggi con JWT API-key robot
 *   - "requires-whitelist"     → richiede estensione whitelist o token utente
 */
export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";
export type AccessLevel = "whitelist" | "requires-whitelist";

export interface EndpointCardData {
  /** id HTML univoco per ancoraggio + Sitelinks (es. "e11", "e22", ...) */
  anchorId: string;
  /** chiave i18n della descrizione lunga in robotsHelpDict.ts (es. "robots.help.e11.desc") */
  descKey: string;
  /** Metodo HTTP */
  method: HttpMethod;
  /** Path con placeholder (es. "/processes/{idtoken}/nextstate/doing") */
  path: string;
  /** Esempio JSON di request body — null per endpoint senza body */
  requestExample: string | null;
  /** Esempio JSON di response 200 OK */
  responseExample: string;
  /** Note di warning opzionali (es. inconsistenza path allegati) */
  warning?: { titleKey: string; parts: string[] };
  /** Legenda dei campi del Request Body (2026-09-10): renderizzata in
   *  RobotsHelp.tsx subito dopo il box "Request Body". Le descrizioni
   *  passano per il dict i18n (`robots.help.legend.body.*`). */
  bodyLegend?: ReadonlyArray<{ name: string; descKey: string }>;
  /** Legenda dei parametri di path/query per gli endpoint GET (2026-09-11):
   *  renderizzata in RobotsHelp.tsx nel box "Path Parameters" prima della
   *  response. Le descrizioni passano per il dict i18n
   *  (`robots.help.legend.param.*` / riuso di `robots.help.legend.body.*`). */
  paramLegend?: ReadonlyArray<{ name: string; descKey: string; isQuery?: boolean }>;
  /** Legenda dei campi della Response (2026-09-11): spiegazione di ogni dato
   *  restituito, renderizzata in RobotsHelp.tsx subito dopo il box
   *  "Response 200 OK". Le descrizioni passano per il dict i18n
   *  (`robots.help.legend.resp.*` / riuso di `legend.body.*`). */
  responseLegend?: ReadonlyArray<{ name: string; descKey: string }>;
  /** Stato whitelist robot — governa il badge colore in RobotsHelp.tsx */
  accessLevel: AccessLevel;
}

export const ROBOTS_HELP_ENDPOINTS: EndpointCardData[] = [
  // ─────── 1. Contesto & Autenticazione ───────
  {
    anchorId: "e11",
    descKey: "robots.help.e11.desc",
    method: "GET",
    path: "/organizations/user",
    requestExample: null,
    // Contratto (verificato live 2026-09-07): il robot vede una sola
    // organizzazione — quella associata al suo JWT.
    responseExample: `{
  "organizations": [
    {
      "id": 14299,
      "name": "robots snippet",
      "administrator": false,
      "subscriptionname": "",
      "hasmultipleuser": false,
      "creation": "2026-09-07T14:00:12.282+02:00",
      "deleted": false,
      "icon": null
    }
  ]
}`,
    responseLegend: [
      { name: "organizations", descKey: "robots.help.legend.resp.organizations" },
      { name: "id", descKey: "robots.help.legend.resp.org.id" },
      { name: "name", descKey: "robots.help.legend.resp.org.name" },
      { name: "administrator", descKey: "robots.help.legend.resp.org.administrator" },
      { name: "subscriptionname", descKey: "robots.help.legend.resp.org.subscriptionname" },
      { name: "hasmultipleuser", descKey: "robots.help.legend.resp.org.hasmultipleuser" },
      { name: "creation", descKey: "robots.help.legend.resp.org.creation" },
      { name: "deleted", descKey: "robots.help.legend.resp.org.deleted" },
      { name: "icon", descKey: "robots.help.legend.resp.org.icon" },
    ],
    accessLevel: "whitelist",
  },

  // ─────── 2. Avvio dei Processi ───────
  {
    anchorId: "e21",
    descKey: "robots.help.e21.desc",
    method: "GET",
    path: "/startevents/organizations/{idorganization}",
    requestExample: null,
    responseExample: `{
  "possiblestart": [
    {
      "id": 21,
      "name": "",
      "idtype": 0,
      "idprocessmodel": 88,
      "processmodelname": "Gestione Ordini",
      "idswimlane": 1,
      "swimlanename": "Lane 1",
      "idorganization": 14207,
      "assignmentmode": 0,
      "userschema": "[]"
    }
  ]
}`,
    paramLegend: [
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
    ],
    responseLegend: [
      { name: "possiblestart", descKey: "robots.help.legend.resp.possiblestart" },
      { name: "id", descKey: "robots.help.legend.resp.start.id" },
      { name: "name", descKey: "robots.help.legend.resp.start.name" },
      { name: "idtype", descKey: "robots.help.legend.resp.start.idtype" },
      { name: "idprocessmodel", descKey: "robots.help.legend.body.idprocessmodel" },
      { name: "processmodelname", descKey: "robots.help.legend.resp.start.processmodelname" },
      { name: "idswimlane", descKey: "robots.help.legend.resp.start.idswimlane" },
      { name: "swimlanename", descKey: "robots.help.legend.resp.start.swimlanename" },
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
      { name: "assignmentmode", descKey: "robots.help.legend.resp.start.assignmentmode" },
      { name: "userschema", descKey: "robots.help.legend.resp.start.userschema" },
    ],
    accessLevel: "whitelist",
  },
  {
    anchorId: "e22",
    descKey: "robots.help.e22.desc",
    method: "POST",
    path: "/processes/",
    // Contratto canonico (2026-09-11, decisione utente): body completo
    // allineato alla shape dell'oggetto `process` restituito da /undo e
    // alla coppia (org, modello) richiesta da /configurations/data.
    requestExample: `{
  "idprocessmodel": 88,
  "idorganization": 14207,
  "idevent": 21,
  "startdate": "2026-08-26T08:00:00Z",
  "enddate": null,
  "description": "Fattura #2026-0451",
  "descriptiondetail": "Emissione automatica da robot",
  "userdata": { "cliente": "Acme Spa" }
}`,
    bodyLegend: [
      { name: "idprocessmodel", descKey: "robots.help.legend.body.idprocessmodel" },
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
      { name: "idevent", descKey: "robots.help.legend.body.idevent" },
      { name: "startdate", descKey: "robots.help.legend.body.startdate" },
      { name: "enddate", descKey: "robots.help.legend.body.enddate" },
      { name: "description", descKey: "robots.help.legend.body.description" },
      { name: "descriptiondetail", descKey: "robots.help.legend.body.descriptiondetail" },
      { name: "userdata", descKey: "robots.help.legend.body.userdata" },
    ],
    responseExample: `{
  "process": {
    "id": 773019
  },
  "processtoken": {
    "id": 901,
    "state": 0
  },
  "previousprocesstoken": {
    "id": 901
  }
}`,
    responseLegend: [
      { name: "process", descKey: "robots.help.legend.resp.process" },
      { name: "processtoken", descKey: "robots.help.legend.resp.processtoken" },
      { name: "id", descKey: "robots.help.legend.resp.token.id" },
      { name: "state", descKey: "robots.help.legend.resp.token.state" },
      { name: "previousprocesstoken", descKey: "robots.help.legend.resp.prevprocesstoken" },
    ],
    accessLevel: "whitelist",
  },

  // ─────── 3. Individuazione Attività (Polling) ───────
  {
    anchorId: "e31",
    descKey: "robots.help.e31.desc",
    method: "GET",
    path: "/tasks/organizations/{idorganization}/canget",
    requestExample: null,
    responseExample: `{
  "waitingflownodes": [
    {
      "idprocesstoken": 901,
      "idflownode": 41,
      "designerid": "Task_EstrarreFatturePDF",
      "flownodename": "EstrarreFatturePDF",
      "idprocess": 773019,
      "idprocessmodel": 88,
      "idorganization": 14207,
      "processmodelname": "Gestione Ordini",
      "state": 0,
      "priority_name": "HIGH",
      "countcomments": 0,
      "countattachments": 2
    }
  ],
  "doingflownodes": []
}`,
    paramLegend: [
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
    ],
    responseLegend: [
      { name: "waitingflownodes", descKey: "robots.help.legend.resp.waitingflownodes" },
      { name: "doingflownodes", descKey: "robots.help.legend.resp.doingflownodes" },
      { name: "idprocesstoken", descKey: "robots.help.legend.resp.task.idprocesstoken" },
      { name: "idflownode", descKey: "robots.help.legend.resp.task.idflownode" },
      { name: "designerid", descKey: "robots.help.legend.resp.task.designerid" },
      { name: "flownodename", descKey: "robots.help.legend.resp.task.flownodename" },
      { name: "idprocess", descKey: "robots.help.legend.resp.task.idprocess" },
      { name: "idprocessmodel", descKey: "robots.help.legend.body.idprocessmodel" },
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
      { name: "processmodelname", descKey: "robots.help.legend.resp.start.processmodelname" },
      { name: "state", descKey: "robots.help.legend.resp.token.state" },
      { name: "priority_name", descKey: "robots.help.legend.resp.task.priority_name" },
      { name: "countcomments", descKey: "robots.help.legend.resp.task.countcomments" },
      { name: "countattachments", descKey: "robots.help.legend.resp.task.countattachments" },
    ],
    accessLevel: "whitelist",
  },
  // ─────── 4. Avanzamento Attività ───────
  {
    anchorId: "e41",
    descKey: "robots.help.e41.desc",
    method: "POST",
    path: "/processes/{idtoken}/nextstate/doing",
    requestExample: `{
  "idflownode": 555,
  "iduserrobot": 17
}`,
    bodyLegend: [
      { name: "idflownode", descKey: "robots.help.legend.body.idflownode" },
      { name: "iduserrobot", descKey: "robots.help.legend.body.iduserrobot" },
    ],
    responseExample: `{
  "idtoken": 888111,
  "state": 1,
  "claimedAt": "2026-08-27T14:33:02Z",
  "iduserrobot": 17
}`,
    responseLegend: [
      { name: "idtoken", descKey: "robots.help.legend.resp.claim.idtoken" },
      { name: "state", descKey: "robots.help.legend.resp.token.state" },
      { name: "claimedAt", descKey: "robots.help.legend.resp.claim.claimedAt" },
      { name: "iduserrobot", descKey: "robots.help.legend.body.iduserrobot" },
    ],
    accessLevel: "whitelist",
  },
  // Undo (spostato dalla vecchia sezione "Recupero Errori" 2026-09-10):
  // annulla la presa in carico, tornando dallo stato Doing a ToDo.
  {
    anchorId: "e52",
    descKey: "robots.help.e52.desc",
    method: "POST",
    path: "/processes/{idtoken}/undo",
    requestExample: null,
    responseExample: `{
  "process": {
    "id": 1,
    "idorganization": 1,
    "idprocessmodel": 1,
    "startdate": "...",
    "enddate": null,
    "userdata": "{}"
  },
  "processtoken": {
    "id": 2,
    "idprocess": 1,
    "idflownode": 7,
    "flownodetype": 1,
    "iduser": 2,
    "state": 0,
    "previous": 0,
    "created": "...",
    "startdoing": null,
    "done": null
  },
  "flownode": {
    "id": 7,
    "name": "Attività 1",
    "designerid": "Activity_1"
  },
  "previousflownode": {
    "id": 6,
    "name": "Evento",
    "designerid": "StartEvent_1"
  },
  "previousprocesstoken": {
    "id": 1,
    "state": 2,
    "idflownode": 6,
    "flownodetype": 0,
    "iduser": 2,
    "previous": 0
  }
}`,
    responseLegend: [
      { name: "process", descKey: "robots.help.legend.resp.process" },
      { name: "processtoken", descKey: "robots.help.legend.resp.processtoken" },
      { name: "id", descKey: "robots.help.legend.resp.token.id" },
      { name: "idprocess", descKey: "robots.help.legend.resp.token.idprocess" },
      { name: "idflownode", descKey: "robots.help.legend.resp.token.idflownode" },
      { name: "flownodetype", descKey: "robots.help.legend.resp.token.flownodetype" },
      { name: "iduser", descKey: "robots.help.legend.resp.token.iduser" },
      { name: "state", descKey: "robots.help.legend.resp.token.state" },
      { name: "previous", descKey: "robots.help.legend.resp.token.previous" },
      { name: "created", descKey: "robots.help.legend.resp.token.created" },
      { name: "startdoing", descKey: "robots.help.legend.resp.token.startdoing" },
      { name: "done", descKey: "robots.help.legend.resp.token.done" },
      { name: "flownode", descKey: "robots.help.legend.resp.flownode" },
      { name: "previousflownode", descKey: "robots.help.legend.resp.prevflownode" },
      { name: "previousprocesstoken", descKey: "robots.help.legend.resp.prevprocesstoken" },
    ],
    accessLevel: "whitelist",
  },
  {
    anchorId: "e42",
    descKey: "robots.help.e42.desc",
    method: "POST",
    path: "/processes/{idtoken}/nextstate/done",
    requestExample: `{
  "idflownode": 555,
  "result": {
    "validated": true,
    "extractedFields": {
      "total": 1234.56,
      "vat": 258.86
    }
  }
}`,
    bodyLegend: [
      { name: "idflownode", descKey: "robots.help.legend.body.idflownode" },
      { name: "result", descKey: "robots.help.legend.body.result" },
    ],
    responseExample: `{
  "process": {
    "id": 1661721,
    "idprocessmodel": 27613,
    "idorganization": 14299,
    "state": 0
  },
  "processtoken": {
    "id": 35093744,
    "idprocess": 1661721,
    "idflownode": 38882,
    "flownodetype": 0,
    "iduser": 21976,
    "state": 2,
    "previous": 35093743,
    "created": "...",
    "startdoing": "...",
    "done": "..."
  },
  "previousprocesstoken": {
    "id": 35093743,
    "state": 2,
    "idflownode": 40414
  },
  "previousflownode": {
    "id": 40414,
    "designerid": "Activity_1",
    "name": "prima task"
  },
  "flownode": {
    "id": 38882,
    "designerid": "Event_1blr1wv",
    "idtype": 1
  },
  "sequenceflows": []
}`,
    responseLegend: [
      { name: "process", descKey: "robots.help.legend.resp.process" },
      { name: "processtoken", descKey: "robots.help.legend.resp.processtoken" },
      { name: "id", descKey: "robots.help.legend.resp.token.id" },
      { name: "idprocess", descKey: "robots.help.legend.resp.token.idprocess" },
      { name: "idflownode", descKey: "robots.help.legend.resp.token.idflownode" },
      { name: "flownodetype", descKey: "robots.help.legend.resp.token.flownodetype" },
      { name: "iduser", descKey: "robots.help.legend.resp.token.iduser" },
      { name: "state", descKey: "robots.help.legend.resp.token.state" },
      { name: "previous", descKey: "robots.help.legend.resp.token.previous" },
      { name: "created", descKey: "robots.help.legend.resp.token.created" },
      { name: "startdoing", descKey: "robots.help.legend.resp.token.startdoing" },
      { name: "done", descKey: "robots.help.legend.resp.token.done" },
      { name: "previousprocesstoken", descKey: "robots.help.legend.resp.prevprocesstoken" },
      { name: "previousflownode", descKey: "robots.help.legend.resp.prevflownode" },
      { name: "flownode", descKey: "robots.help.legend.resp.flownode" },
      { name: "sequenceflows", descKey: "robots.help.legend.resp.sequenceflows" },
    ],
    accessLevel: "whitelist",
  },

  // ─────── 5. Scelta del Percorso (Gateway) ───────
  {
    anchorId: "e51",
    descKey: "robots.help.e51.desc",
    method: "POST",
    path: "/processes/tokens/{idtoken}/exit/{designerid}",
    requestExample: `{
  "reason": "validated_by_robot",
  "metadata": {
    "validationScore": 0.97
  }
}`,
    bodyLegend: [
      { name: "reason", descKey: "robots.help.legend.body.reason" },
      { name: "metadata", descKey: "robots.help.legend.body.metadata" },
    ],
    responseExample: `{
  "idtoken": 888111,
  "selectedExit": "approve",
  "designerid": "Gateway_approve_01",
  "advancedAt": "2026-08-27T14:34:01Z"
}`,
    responseLegend: [
      { name: "idtoken", descKey: "robots.help.legend.resp.claim.idtoken" },
      { name: "selectedExit", descKey: "robots.help.legend.resp.gw.selectedExit" },
      { name: "designerid", descKey: "robots.help.legend.resp.task.designerid" },
      { name: "advancedAt", descKey: "robots.help.legend.resp.gw.advancedAt" },
    ],
    accessLevel: "whitelist",
  },

  // ─────── 6. Allegati ───────
  // NOTA: l'endpoint POST /flownodes/{idprocesstoken}/comments è stato rimosso
  // dalla documentazione (2026-09-09): un robot non può commentare —
  // comments.iduser ha una FK su users(id) e un robot non è una riga di users,
  // quindi l'insert viene rifiutato dal server (500 mascherato).
  {
    anchorId: "e62",
    descKey: "robots.help.e62.desc",
    method: "POST",
    path: "/flownodes/{idprocesstoken}/attachments",
    // Contratto canonico (2026-09-11, decisione utente): JSON con blob base64,
    // coerente con i campi restituiti da /attachments/light (idtype,
    // cloudlink, description).
    requestExample: `{
  "idorganization": 14207,
  "filename": "fattura_2026.pdf",
  "cloudlink": "",
  "idtype": 1,
  "description": "Fattura 2026",
  "blob": "<base64 del file>",
  "size": 24576
}`,
    bodyLegend: [
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
      { name: "filename", descKey: "robots.help.legend.body.filename" },
      { name: "cloudlink", descKey: "robots.help.legend.body.cloudlink" },
      { name: "idtype", descKey: "robots.help.legend.body.idtype" },
      { name: "description", descKey: "robots.help.legend.body.description" },
      { name: "blob", descKey: "robots.help.legend.body.blob" },
      { name: "size", descKey: "robots.help.legend.body.size" },
    ],
    responseExample: `{
  "idattachment": 7890,
  "idprocesstoken": 888111,
  "filename": "fattura_2026.pdf",
  "size": 24576,
  "uploadedAt": "2026-08-27T14:34:10Z"
}`,
    responseLegend: [
      { name: "idattachment", descKey: "robots.help.legend.resp.attw.idattachment" },
      { name: "idprocesstoken", descKey: "robots.help.legend.resp.task.idprocesstoken" },
      { name: "filename", descKey: "robots.help.legend.body.filename" },
      { name: "size", descKey: "robots.help.legend.body.size" },
      { name: "uploadedAt", descKey: "robots.help.legend.resp.attw.uploadedAt" },
    ],
    warning: {
      titleKey: "robots.help.cat6.warning.title",
      parts: [
        "robots.help.cat6.warning.p1",
        "robots.help.cat6.warning.p2",
        "robots.help.cat6.warning.p3",
        "robots.help.cat6.warning.p4",
        "robots.help.cat6.warning.p5",
        "robots.help.cat6.warning.p6",
        "robots.help.cat6.warning.p7",
      ],
    },
    accessLevel: "whitelist",
  },
  {
    anchorId: "e63",
    descKey: "robots.help.e63.desc",
    method: "GET",
    path: "/processes/{idprocess}/attachments/light",
    requestExample: null,
    // Contratto (verificato live 2026-09-09): {"attachments": [...]} — come
    // l'endpoint non-light ma senza il blocco di paginazione.
    responseExample: `{
  "attachments": [
    {
      "id": 7890,
      "idorganization": 1,
      "filename": "invoice-98765.pdf",
      "description": "",
      "idtype": 1,
      "cloudlink": "",
      "creation": "2026-09-09T14:34:10Z",
      "iduser": 0,
      "username": "",
      "activityname": "acct1"
    }
  ]
}`,
    paramLegend: [
      { name: "idprocess", descKey: "robots.help.legend.param.idprocess" },
    ],
    responseLegend: [
      { name: "attachments", descKey: "robots.help.legend.resp.attachments" },
      { name: "id", descKey: "robots.help.legend.resp.att.id" },
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
      { name: "filename", descKey: "robots.help.legend.body.filename" },
      { name: "description", descKey: "robots.help.legend.resp.att.description" },
      { name: "idtype", descKey: "robots.help.legend.body.idtype" },
      { name: "cloudlink", descKey: "robots.help.legend.body.cloudlink" },
      { name: "creation", descKey: "robots.help.legend.resp.att.creation" },
      { name: "iduser", descKey: "robots.help.legend.resp.att.iduser" },
      { name: "username", descKey: "robots.help.legend.resp.att.username" },
      { name: "activityname", descKey: "robots.help.legend.resp.att.activityname" },
    ],
    accessLevel: "whitelist",
  },

  // ─────── 7. Pull esteso dei task (whitelist + requires-whitelist) ───────
  {
    anchorId: "e13",
    descKey: "robots.help.e13.desc",
    method: "GET",
    path: "/tasks/organizations/{idorganization}/doing",
    requestExample: null,
    responseExample: `{
  "doingflownodes": [
    {
      "idprocesstoken": 1,
      "idprocess": 1,
      "idflownode": 7,
      "flownodename": "Attività 1",
      "priority_name": "Alta"
    }
  ]
}`,
    paramLegend: [
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
    ],
    responseLegend: [
      { name: "doingflownodes", descKey: "robots.help.legend.resp.doingflownodes" },
      { name: "idprocesstoken", descKey: "robots.help.legend.resp.task.idprocesstoken" },
      { name: "idprocess", descKey: "robots.help.legend.resp.task.idprocess" },
      { name: "idflownode", descKey: "robots.help.legend.resp.task.idflownode" },
      { name: "flownodename", descKey: "robots.help.legend.resp.task.flownodename" },
      { name: "priority_name", descKey: "robots.help.legend.resp.task.priority_name" },
    ],
    accessLevel: "requires-whitelist",
  },

  // ─────── 9. Discovery & configuration ───────
  {
    anchorId: "e71",
    descKey: "robots.help.e71.desc",
    method: "GET",
    path: "/startevents",
    requestExample: null,
    responseExample: `{
  "possiblestart": [
    {
      "id": 1,
      "name": "StartEvent_1",
      "idtype": 0,
      "idprocessmodel": 1,
      "processmodelname": "Modello A",
      "idswimlane": 1,
      "swimlanename": "Lane 1",
      "idorganization": 1,
      "assignmentmode": 1,
      "userschema": "..."
    }
  ]
}`,
    responseLegend: [
      { name: "possiblestart", descKey: "robots.help.legend.resp.possiblestart" },
      { name: "id", descKey: "robots.help.legend.resp.start.id" },
      { name: "name", descKey: "robots.help.legend.resp.start.name" },
      { name: "idtype", descKey: "robots.help.legend.resp.start.idtype" },
      { name: "idprocessmodel", descKey: "robots.help.legend.body.idprocessmodel" },
      { name: "processmodelname", descKey: "robots.help.legend.resp.start.processmodelname" },
      { name: "idswimlane", descKey: "robots.help.legend.resp.start.idswimlane" },
      { name: "swimlanename", descKey: "robots.help.legend.resp.start.swimlanename" },
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
      { name: "assignmentmode", descKey: "robots.help.legend.resp.start.assignmentmode" },
      { name: "userschema", descKey: "robots.help.legend.resp.start.userschema" },
    ],
    accessLevel: "whitelist",
  },
  {
    anchorId: "e72",
    descKey: "robots.help.e72.desc",
    method: "GET",
    path: "/robots/configurations/data/{idorganization}/{idprocessmodel}",
    requestExample: null,
    // Contratto (verificato live 2026-09-09): singolo oggetto, non un array.
    responseExample: `{
  "id": 1,
  "idorganization": 1,
  "idrobot": 1,
  "idprocessmodel": 1,
  "linkedto": "",
  "datajson": {
    "chiave": "valore"
  }
}`,
    paramLegend: [
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
      { name: "idprocessmodel", descKey: "robots.help.legend.body.idprocessmodel" },
    ],
    responseLegend: [
      { name: "id", descKey: "robots.help.legend.resp.cfg.id" },
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
      { name: "idrobot", descKey: "robots.help.legend.resp.cfg.idrobot" },
      { name: "idprocessmodel", descKey: "robots.help.legend.body.idprocessmodel" },
      { name: "linkedto", descKey: "robots.help.legend.resp.cfg.linkedto" },
      { name: "datajson", descKey: "robots.help.legend.resp.datajson" },
    ],
    accessLevel: "whitelist",
  },
  {
    anchorId: "e73",
    descKey: "robots.help.e73.desc",
    method: "GET",
    path: "/robots/organizations",
    requestExample: null,
    responseExample: `{
  "idrobot": 1,
  "organizations": [
    {
      "id": 1,
      "name": "Org1",
      "tags": [
        {
          "id": 1,
          "name": "urgente",
          "idorganization": 1,
          "color": "#ff0000"
        }
      ]
    }
  ]
}`,
    responseLegend: [
      { name: "idrobot", descKey: "robots.help.legend.resp.cfg.idrobot" },
      { name: "organizations", descKey: "robots.help.legend.resp.organizations" },
      { name: "id", descKey: "robots.help.legend.resp.org.id" },
      { name: "name", descKey: "robots.help.legend.resp.org.name" },
      { name: "tags", descKey: "robots.help.legend.resp.tags" },
      { name: "tags.id", descKey: "robots.help.legend.resp.tag.id" },
      { name: "tags.name", descKey: "robots.help.legend.resp.tag.name" },
      { name: "tags.idorganization", descKey: "robots.help.legend.body.idorganization" },
      { name: "tags.color", descKey: "robots.help.legend.resp.tag.color" },
    ],
    accessLevel: "whitelist",
  },
  {
    anchorId: "e74",
    descKey: "robots.help.e74.desc",
    method: "GET",
    path: "/organizations/{idorganization}/robots/{id}/settings?idprocessmodel={idprocessmodel}",
    requestExample: null,
    responseExample: `{
  "id": 1,
  "idorganization": 1,
  "idrobot": 1,
  "idprocessmodel": null,
  "linkedto": "",
  "datajson": {
    "properties": {
      "campo1 ssss": {
        "type": "...",
        "value": "Test"
      }
    }
  }
}`,
    paramLegend: [
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
      { name: "id", descKey: "robots.help.legend.param.id" },
      { name: "idprocessmodel", descKey: "robots.help.legend.body.idprocessmodel", isQuery: true },
    ],
    responseLegend: [
      { name: "id", descKey: "robots.help.legend.resp.cfg.id" },
      { name: "idorganization", descKey: "robots.help.legend.body.idorganization" },
      { name: "idrobot", descKey: "robots.help.legend.resp.cfg.idrobot" },
      { name: "idprocessmodel", descKey: "robots.help.legend.body.idprocessmodel" },
      { name: "linkedto", descKey: "robots.help.legend.resp.cfg.linkedto" },
      { name: "datajson", descKey: "robots.help.legend.resp.datajson" },
      { name: "properties", descKey: "robots.help.legend.resp.cfg.properties" },
    ],
    accessLevel: "whitelist",
  },

];

/** Raggruppa gli endpoint per categoria (cat1..cat9) per il render.
 *  Lista di anchorId — il lookup completo avviene via ROBOTS_HELP_ENDPOINTS_BY_ID.
 *  Le sezioni 10/11/13 (allegati read, userdata, lifecycle) e la sezione Admin
 *  (catA) sono state rimosse dalla documentazione (2026-09-04).
 *  Dal 2026-09-10 la cat8 "Recupero Errori" non esiste più come sezione: l'undo
 *  (e52) è stato spostato in cat4 "Avanzamento delle Attività". */
export const ENDPOINTS_BY_CATEGORY: Record<string, string[]> = {
  cat1: ["e11"],
  cat2: ["e21", "e22"],
  cat3: ["e31"],
  cat4: ["e41", "e42", "e52"],
  cat5: ["e51"],
  cat6: ["e62", "e63"],
  cat7: ["e13"],
  cat9: ["e71", "e72", "e73", "e74"],
};

/** Lookup veloce per anchorId → EndpointCardData (usato dal render). */
export const ROBOTS_HELP_ENDPOINTS_BY_ID: Record<string, EndpointCardData> =
  ROBOTS_HELP_ENDPOINTS.reduce(
    (acc, ep) => {
      acc[ep.anchorId] = ep;
      return acc;
    },
    {} as Record<string, EndpointCardData>
  );
